import React, { useState } from 'react'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { IContextMenuState } from 'types'
import Button from 'components/button'
import Checkbox from 'components/checkbox'
import Input from 'components/input'
import Menu from 'components/menu'
import { MenuItem, MenuList, MenuSeparator } from 'components/menu/items'
import { popoverPlacement } from 'components/popover'
import { ChevronDown } from 'components/icons'
import Tooltip from 'components/tooltip'
import useFocusWhenOpen from 'hooks/useFocusWhenOpen'

const WIDTH = 280

/** Classes for a button that opens a menu in a form: as wide as the field, and as tall as a text box. */
export const FIELD_MENU_BUTTON = 'h-9 w-full justify-between gap-1.5 rounded-lg px-3 font-normal text-body'

export interface FilterOption {
  value: string
  label: string
}

/** Options under a heading of their own, which ticks them all; a group without one is a plain list. */
export interface FilterGroup {
  label?: string
  options: FilterOption[]
}

/** What a filter's button says: "All users", "No users", "1 user", "3 users". */
export const filterLabel = (noun: { one: string; many: string }, selected: string[] | null): string => {
  if (selected === null) return `No ${noun.many}`
  if (selected.length === 0) return `All ${noun.many}`
  return `${selected.length} ${selected.length === 1 ? noun.one : noun.many}`
}

/** A tick for a menu row: only shows the row's state, as the row takes the click. */
const Tick: React.FC<{ checked: boolean; indeterminate?: boolean }> = ({ checked, indeterminate }) => (
  <span className="pointer-events-none flex items-center [&>div]:flex" aria-hidden>
    <Checkbox checked={checked} indeterminate={indeterminate} onChange={() => undefined} className="my-0" />
  </span>
)

interface FilterMenuProps {
  /** What it filters by, for its button and search: { one: 'user', many: 'users' }. */
  noun: { one: string; many: string }
  groups: FilterGroup[]
  /**
   * The values chosen. As a filter: [] for all of them (nothing filtered out), null for none at all. With
   * `pick`: just the values ticked, [] for none.
   */
  selected: string[] | null
  onChange: (selected: string[] | null) => void
  loading?: boolean
  /** Picking values rather than filtering by them: what's ticked is the choice, and Reset is Clear. */
  pick?: boolean
  /** What the button says, in place of the count. */
  buttonLabel?: React.ReactNode
  /** Classes for the button, as a width for a column of them. */
  buttonClassName?: string
  /** Its accessible name, when the button's own words don't say what it's for. */
  label?: string
  /** The button's label in full, shown on hover when it's cut short: a long list of what's chosen. */
  labelInFull?: string
  /** The menu as wide as the button and lined up with it, for a button as wide as a form's field. */
  matchWidth?: boolean
}

/**
 * A filter on our menu, choosing any number of values with ticks: a search at the top, the values (under
 * headings that tick a group at once), and at the foot a Select all tick and Reset (back to everything,
 * the search cleared). The button, as wide as what it says, gives the choice as a count ("3 users").
 * Every value shows ticked when nothing is filtered out; unticking them all chooses none. With `pick` it
 * chooses values outright, as a member's tags: what's ticked is the choice, and its foot clears it.
 */
const FilterMenu: React.FC<FilterMenuProps> = ({
  noun,
  groups,
  selected,
  onChange,
  loading,
  pick = false,
  buttonLabel,
  buttonClassName = '',
  label,
  labelInFull,
  matchWidth = false
}) => {
  const [menu, setMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [width, setWidth] = useState(WIDTH)
  const [search, setSearch] = useState('')
  // The ticks while the menu is open: every value while nothing is filtered out.
  const [ticked, setTicked] = useState<string[]>([])
  const searchInput = useFocusWhenOpen(menu.open)

  const all = groups.flatMap((group) => group.options.map((option) => option.value))
  const query = search.trim().toLowerCase()
  const shown = groups
    .map((group) => ({
      ...group,
      options: query
        ? group.options.filter(
            (option) => option.label.toLowerCase().includes(query) || !!group.label?.toLowerCase().includes(query)
          )
        : group.options
    }))
    .filter((group) => group.options.length > 0)

  /** Ticks these, and filters by them: nothing filtered out when every value is ticked, null for none. */
  const tick = (next: string[]) => {
    setTicked(next)
    if (pick) onChange(next)
    else if (next.length === 0) onChange(null)
    else onChange(next.length === all.length ? [] : next)
  }
  const toggle = (value: string) =>
    tick(ticked.includes(value) ? ticked.filter((each) => each !== value) : [...ticked, value])
  const toggleGroup = (values: string[]) => {
    const every = values.every((value) => ticked.includes(value))
    tick(every ? ticked.filter((each) => !values.includes(each)) : [...new Set([...ticked, ...values])])
  }

  const open = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTicked(selected === null || (pick && selected.length === 0) ? [] : selected.length === 0 ? all : selected)
    setSearch('')
    const rect = event.currentTarget.getBoundingClientRect()
    const menuWidth = matchWidth ? rect.width : WIDTH
    setWidth(menuWidth)
    setMenu(popoverPlacement(rect, { width: menuWidth, height: 460, align: matchWidth ? 'start' : 'end' }))
  }

  return (
    <>
      <Button
        internalType="outline"
        aria-haspopup="menu"
        aria-expanded={menu.open}
        isLoading={loading}
        aria-label={label}
        className={`h-8 gap-1.5 px-2.5 font-normal text-body ${buttonClassName}`}
        // The menu closes on a press outside it, and this button counts as outside: while it's open, keep the
        // press from reaching it and let the click close it instead.
        onMouseDown={(event: React.MouseEvent) => {
          if (menu.open) event.stopPropagation()
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          menu.open ? setMenu(INITIAL_CONTEXT_MENU_STATE) : open(event)
        }
      >
        {/* A full line for the label, as the button sets its line tight: a truncated label would cut descenders. */}
        {labelInFull ? (
          <Tooltip
            content={labelInFull}
            showOnlyIfTruncated
            // Hidden while the menu, which lists them all, is open.
            visible={menu.open ? false : undefined}
            className="w-max max-w-[320px] whitespace-normal"
          >
            <span className="truncate leading-5">{buttonLabel ?? filterLabel(noun, selected)}</span>
          </Tooltip>
        ) : (
          <span className="truncate leading-5">{buttonLabel ?? filterLabel(noun, selected)}</span>
        )}
        <ChevronDown />
      </Button>
      {menu.open && (
        <Menu id={`filter-${noun.many}`} menuState={menu} setMenuState={setMenu} width={width} fixed>
          <div className="border-b border-divider p-2">
            <Input
              ref={searchInput}
              type="search"
              aria-label={`Search ${noun.many}`}
              placeholder={`Search ${noun.many}`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-8"
            />
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            <MenuList>
              {shown.length === 0 && (
                <div className="px-2 py-1.5 text-[13px] text-muted">
                  {all.length === 0 ? `No ${noun.many}` : `No ${noun.many} match.`}
                </div>
              )}
              {shown.map((group, index) => {
                const values = group.options.map((option) => option.value)
                const chosen = values.filter((value) => ticked.includes(value)).length
                return (
                  <React.Fragment key={group.label ?? index}>
                    {index > 0 && <MenuSeparator />}
                    {group.label && (
                      <MenuItem
                        icon={
                          <Tick
                            checked={chosen === values.length}
                            indeterminate={chosen > 0 && chosen < values.length}
                          />
                        }
                        onClick={() => toggleGroup(values)}
                      >
                        <span className="font-medium">{group.label}</span>
                      </MenuItem>
                    )}
                    {group.options.map((option) => (
                      <MenuItem
                        key={option.value}
                        icon={<Tick checked={ticked.includes(option.value)} />}
                        className={group.label ? 'pl-8' : undefined}
                        onClick={() => toggle(option.value)}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </React.Fragment>
                )
              })}
            </MenuList>
          </div>
          <div className="flex items-center justify-between border-t border-divider py-1 pr-1.5 pl-1">
            <MenuItem
              icon={
                <Tick
                  checked={all.length > 0 && ticked.length === all.length}
                  indeterminate={ticked.length > 0 && ticked.length < all.length}
                />
              }
              className="w-auto"
              onClick={() => tick(ticked.length === all.length ? [] : all)}
            >
              Select all
            </MenuItem>
            <Button
              internalType="transparent"
              className="h-7 px-2 text-[12px] font-medium text-body"
              onClick={() => {
                setSearch('')
                tick(pick ? [] : all)
              }}
            >
              {pick ? 'Clear' : 'Reset'}
            </Button>
          </div>
        </Menu>
      )}
    </>
  )
}

export default FilterMenu
